import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from "recharts";

export interface DonutChartWidgetProps {
  data: Array<{ name: string; value: number; color?: string }>;
  innerRadius?: number;
  outerRadius?: number;
  showLegend?: boolean;
  formatValue?: (value: number) => string;
  /** Custom formatter for the center total label. Receives the sum of all values. */
  formatCenterLabel?: (total: number) => string;
  height?: number;
  colors?: string[];
  centerLabel?: string;
}

const DEFAULT_COLORS = [
  "#0ea5e9", // sky-500
  "#10b981", // emerald-500
  "#f59e0b", // amber-500
  "#f43f5e", // rose-500
  "#8b5cf6", // violet-500
  "#06b6d4", // cyan-500
  "#ec4899", // pink-500
  "#64748b", // slate-500
];

function CustomTooltip({ active, payload, formatValue }: any) {
  if (!active || !payload?.length) return null;

  const entry = payload[0];
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2">
      <div className="flex items-center gap-2 text-sm">
        <span
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: entry.payload.fill }}
        />
        <span className="text-gray-600">{entry.name}:</span>
        <span className="font-medium text-gray-900">
          {formatValue ? formatValue(entry.value) : entry.value}
        </span>
      </div>
    </div>
  );
}

function CustomLegend({ payload }: any) {
  if (!payload?.length) return null;

  return (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 pt-2">
      {payload.map((entry: any, index: number) => (
        <div key={index} className="flex items-center gap-1.5 text-xs">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-gray-600">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

export function DonutChartWidget({
  data,
  innerRadius = 60,
  outerRadius = 100,
  showLegend = true,
  formatValue,
  formatCenterLabel,
  height = 280,
  colors = DEFAULT_COLORS,
  centerLabel,
}: DonutChartWidgetProps) {
  const filteredData = data.filter((d) => d.value > 0);

  if (!filteredData.length) {
    return (
      <div
        className="flex items-center justify-center text-gray-400 text-sm"
        style={{ height }}
      >
        Inte tillräckligt med data
      </div>
    );
  }

  const total = filteredData.reduce((sum, d) => sum + d.value, 0);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={filteredData}
          cx="50%"
          cy="45%"
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          paddingAngle={2}
          dataKey="value"
          nameKey="name"
          strokeWidth={0}
        >
          {filteredData.map((entry, index) => (
            <Cell
              key={entry.name}
              fill={entry.color || colors[index % colors.length]}
            />
          ))}
        </Pie>
        <Tooltip
          content={<CustomTooltip formatValue={formatValue} />}
        />
        {showLegend && <Legend content={<CustomLegend />} />}
        {/* Center label */}
        {centerLabel !== undefined && innerRadius > 0 && (
          <text
            x="50%"
            y="42%"
            textAnchor="middle"
            dominantBaseline="central"
            className="text-xl font-bold"
            fill="#111827"
          >
            {formatCenterLabel
              ? formatCenterLabel(total)
              : total.toLocaleString("sv-SE")}
          </text>
        )}
      </PieChart>
    </ResponsiveContainer>
  );
}
